import { toast } from '@raystack/apsara';
import { getPolygonArea } from '@studio/map';
import moment from 'moment';

import store from '@src/redux/store';
import { SimplifyPolygon } from '@src/utils/AoiUtils';
import { setEndDate } from '@src/utils/common';
import { UTM_CONSTELLATIONS } from '@src/utils/constants/LENS_CONSTANTS';

export const LensErrorHelper = error => {
  if (error?.response?.status == 400) {
    console.error(error.response.data);
    toast.error('Something went wrong!', {
      description: `${error.response.data.detail}`
    });
    return;
  }
  toast.error('Oops', {
    description: 'Something went wrong while fetching images! Please try again.'
  });
  console.error(error);
};

export const RunImageSearch = (trigger, geometry) => {
  const state = store.getState();
  const filters = state.filters;

  if (!filters.satellite) {
    toast.warning('No satellites selected!', {
      description: 'Please choose atleast 1 satellite constellation first.'
    });
    return;
  }
  if (!geometry || Object.keys(geometry).length === 0) {
    toast.warning('No area selected!', {
      description:
        'Please draw and AOI or upload a .geojson/kml file to define AOI boundary.'
    });
    return;
  }

  const satellite_list = filters.satellite.map(item => {
    return {
      satellite_id: item.satellite_id,
      collection_id: item.collection_id
    };
  });

  const filters_list = [];

  // push cloud cover filter if on
  if (filters.filterStates.showCloudCoverage) {
    let obj = {
      id: 'eo:cloud_cover',
      min: filters.cloudCoverage[0],
      max: filters.cloudCoverage[1]
    };
    filters_list.push(obj);
  }

  // push gsd filter if on
  if (filters.filterStates.showGSD) {
    const obj = {
      id: 'gsd',
      min: filters.spatialResolution[0],
      max: filters.spatialResolution[1]
    };
    filters_list.push(obj);
  }

  // push off_nadir filter if on
  if (filters.filterStates.showAngle) {
    const obj = {
      id: 'off_nadir',
      min: filters.offNadirAngle[0],
      max: filters.offNadirAngle[1]
    };
    filters_list.push(obj);
  }

  trigger({
    start_date: filters.startDate,
    end_date: setEndDate(filters.endDate),
    satellites: satellite_list,
    filters: filters_list,
    geometry: SimplifyPolygon(geometry)
    // provider: filters.provider,
  });
};

// prepare image data array
function prepareData(data, sortOrder, constellations) {
  // console.log("prepare data", data);
  const array = Object.keys(data).map(image => {
    const currImg = data[image];

    const currArea = UTM_CONSTELLATIONS.has(currImg.collection_id)
      ? getPolygonArea.utm(currImg.geometry)
      : getPolygonArea(currImg.geometry);

    return {
      id: image,
      name:
        constellations?.find(d => d.id === currImg.constellation_id)?.name ||
        '',
      imageID: image.split('_', 2),
      url: currImg.preview_url,
      stac_url: currImg.stac_url,
      datetime: currImg.datetime,
      area: currArea,
      cloud: currImg['eo:cloud_cover'],
      gsd: currImg.gsd,
      angle: '-',
      bands: currImg.bands,
      satellite: currImg?.platform ? currImg.platform : currImg.instruments[0],
      geometry: currImg.geometry,
      sun_elevation: currImg['view:sun_elevation'],
      satellite_look_angle: currImg['view:satellite_look_angle'],
      off_nadir: currImg['view:off_nadir'],
      sun_azimuth: currImg['view:sun_azimuth'],
      collection_id: currImg.collection_id,
      provider: currImg.provider,
      constellation_id: currImg.constellation_id,
      is_accessible: currImg.is_accessible
    };
  });
  // console.log("Processed image list array", array);
  if (sortOrder === 'descend') {
    array.sort(
      (a, b) =>
        new moment.utc(b.datetime).format('YYYYMMDD') -
        new moment.utc(a.datetime).format('YYYYMMDD')
    );
    return array;
  } else {
    array.sort(
      (a, b) =>
        new moment.utc(a.datetime).format('YYYYMMDD') -
        new moment.utc(b.datetime).format('YYYYMMDD')
    );
    return array;
  }
}

export const ImageSearchHelper = (
  data,
  setImages,
  content,
  setContent,
  sortOrder,
  currentPage,
  setCurrentPage,
  setPageNumbers,
  constellations
) => {
  if (data !== null) {
    if (Object.keys(data).length === 0) {
      setContent({
        posts: [],
        postesPerPage: 20
      });
      setImages([]);
      return;
    }
    let array = prepareData(data, sortOrder, constellations);
    setImages(array);
    // if tiles greater then 20, then paginate
    if (array.length > 20) {
      let IndLastPost = currentPage * content.postesPerPage;
      let IndFistPost = IndLastPost - content.postesPerPage;

      setContent({
        posts: array.slice(IndFistPost, IndLastPost),
        postesPerPage: content.postesPerPage
      });
      let pages = [];
      for (
        let i = 1;
        i <= Math.ceil(array.length / content.postesPerPage);
        i++
      ) {
        pages.push(i);
      }
      setPageNumbers(pages);
      setCurrentPage(1);
    } else {
      setContent({
        posts: array,
        postesPerPage: content.postesPerPage
      });
      setPageNumbers([]);
    }

    return;
  }
  setImages([]);
  toast.error('Oops!', {
    description:
      "We're having some trouble with image search right now. We're working on it and hope to have it fixed soon!"
  });
};
